const PersonalisationUtils = (() => {
  const DEFAULT_CHARACTER_LIMIT = 5;
  const TEXT_PROPERTY_KEYS = ['Text', 'Personalisation Text'];
  const COLOUR_PROPERTY_KEYS = ['Colour', 'Personalisation Colour'];
  const PERSONALISATION_UNITS_KEY = '_personalisation_units';

  const normaliseCharacterLimit = (value, fallback = DEFAULT_CHARACTER_LIMIT) => {
    const parsedValue = Number.parseInt(value, 10);
    return Number.isInteger(parsedValue) && parsedValue > 0 ? parsedValue : fallback;
  };

  const normalisePersonalisationState = (state = {}, limit = DEFAULT_CHARACTER_LIMIT) => {
    const characterLimit = normaliseCharacterLimit(limit);
    const text = [...String(state.text || '')].slice(0, characterLimit).join('');
    const icon = state.icon && state.icon.name
      ? { name: String(state.icon.name), imageUrl: String(state.icon.imageUrl || '') }
      : null;

    return { text, icon };
  };

  const serializePersonalisation = (state = {}) => {
    const iconToken = state.icon && state.icon.name ? `[${state.icon.name}]` : '';
    return `${iconToken}${state.text || ''}`.trim();
  };

  const countTextCharacters = (value = '') => [...String(value)].length;

  const countSerializedPersonalisationUnits = (value = '') => {
    const serializedValue = String(value);
    const iconMatch = serializedValue.match(/^\[[^\]]+\]/);
    const text = iconMatch ? serializedValue.slice(iconMatch[0].length) : serializedValue;

    return countTextCharacters(text) + (iconMatch ? 1 : 0);
  };

  const getPersonalisationUnits = (propertiesLike) => {
    if (!propertiesLike) return 0;

    const getValue = (key) =>
      typeof propertiesLike.get === 'function'
        ? propertiesLike.get(`properties[${key}]`)
        : propertiesLike[key];
    const storedUnits = Number.parseInt(getValue(PERSONALISATION_UNITS_KEY), 10);

    if (Number.isInteger(storedUnits) && storedUnits > 0) return storedUnits;

    const serializedValue = TEXT_PROPERTY_KEYS.map(getValue).find(Boolean) || '';
    return countSerializedPersonalisationUnits(serializedValue);
  };

  const calculatePersonalisationFeeQuantity = (productQuantity, personalisationUnits) => {
    const parsedProductQuantity = Number.parseInt(productQuantity, 10);
    const parsedPersonalisationUnits = Number.parseInt(personalisationUnits, 10);
    const quantity = Number.isInteger(parsedProductQuantity) && parsedProductQuantity > 0
      ? parsedProductQuantity
      : 1;
    const units = Number.isInteger(parsedPersonalisationUnits) && parsedPersonalisationUnits > 0
      ? parsedPersonalisationUnits
      : 0;

    return quantity * units;
  };

  const firstPropertyValue = (properties = {}, keys = []) => {
    for (const key of keys) {
      if (properties[key]) return properties[key];
    }
    return '';
  };

  const getPersonalisationProperties = (properties = {}) => ({
    text: firstPropertyValue(properties, TEXT_PROPERTY_KEYS),
    colour: firstPropertyValue(properties, COLOUR_PROPERTY_KEYS),
  });

  const hasPersonalisationText = (propertiesLike) => {
    if (!propertiesLike) return false;

    if (typeof propertiesLike.get === 'function') {
      return TEXT_PROPERTY_KEYS.some((key) => propertiesLike.get(`properties[${key}]`));
    }

    return Boolean(getPersonalisationProperties(propertiesLike).text);
  };

  const buildPersonalisationFeeUpdates = (items = []) => {
    const parentFeeQuantityByKey = new Map();
    const parentFeeQuantityByVariant = new Map();

    items.forEach((item) => {
      if (!hasPersonalisationText(item.properties)) return;

      const feeQuantity = item.quantity * getPersonalisationUnits(item.properties);
      parentFeeQuantityByKey.set(String(item.key), feeQuantity);
      const variantId = String(item.variant_id);
      parentFeeQuantityByVariant.set(
        variantId,
        (parentFeeQuantityByVariant.get(variantId) || 0) + feeQuantity
      );
    });

    return items
      .filter((item) => item.properties && item.properties._personalisation_fee === 'true')
      .map((fee) => {
        const parentKey = String(fee.properties._parent_key || '');
        const parentVariantId = String(fee.properties._parent_id || '');
        const parentQuantity = parentKey
          ? parentFeeQuantityByKey.get(parentKey)
          : parentFeeQuantityByVariant.get(parentVariantId);
        const quantity = parentQuantity === undefined ? 0 : parentQuantity;

        return fee.quantity === quantity ? null : { id: fee.key, quantity };
      })
      .filter(Boolean);
  };

  return {
    DEFAULT_CHARACTER_LIMIT,
    TEXT_PROPERTY_KEYS,
    COLOUR_PROPERTY_KEYS,
    PERSONALISATION_UNITS_KEY,
    normaliseCharacterLimit,
    normalisePersonalisationState,
    serializePersonalisation,
    countTextCharacters,
    countSerializedPersonalisationUnits,
    getPersonalisationUnits,
    calculatePersonalisationFeeQuantity,
    getPersonalisationProperties,
    hasPersonalisationText,
    buildPersonalisationFeeUpdates,
  };
})();

if (typeof window !== 'undefined') window.PersonalisationUtils = PersonalisationUtils;
if (typeof module !== 'undefined') module.exports = PersonalisationUtils;
